// src/modules/quotes/QuoteDetail.jsx
import { useState } from "react";
import { C } from "../../constants";
import { Ic } from "../../components/ui/Icons";
import { apiCall } from "../../api";
import FileUpload from "../../components/ui/FileUpload";
import UpdateQuoteModal from "./UpdateQuoteModal";
import SubmissionDetail from "../projects/SubmissionDetail";

const STATUS_OPTIONS = ["Open", "Closed - Lost", "Ordered"];
const STATUS_COLORS = {
  "Open":          { bg: "#DBEAFE", c: "#1E40AF" },
  "Closed - Lost": { bg: "#FEE2E2", c: "#991B1B" },
  "Ordered":       { bg: "#D1FAE5", c: "#065F46" },
};

function fmtDate(val) {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d)) return val;
  return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
}

function Field({ label, value, children }) {
  return (
    <div style={{ padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 11, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{label}</div>
      {children || <div style={{ fontSize: 14, color: value ? C.t1 : C.t3 }}>{value || "—"}</div>}
    </div>
  );
}

function Section({ title }) {
  return (
    <div style={{ padding: "16px 0 8px", borderBottom: `2px solid ${C.navy}15` }}>
      <h4 style={{ fontSize: 14, fontWeight: 700, color: C.navy, margin: 0 }}>{title}</h4>
    </div>
  );
}

function DocRow({ doc, onDelete, isAdmin }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, background: C.grayBg, border: `1px solid ${C.border}`, marginBottom: 6 }}>
      <span style={{ fontSize: 16 }}>📄</span>
      <span onClick={() => window.open(doc.url, "_blank")}
        style={{ flex: 1, fontSize: 13, color: C.info, cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        onMouseEnter={e => e.target.style.textDecoration = "underline"}
        onMouseLeave={e => e.target.style.textDecoration = "none"}>
        {doc.name}
      </span>
      {isAdmin && onDelete && (
        <button onClick={() => onDelete(doc.id)}
          style={{ background: "none", border: "none", cursor: "pointer", color: C.t3, fontSize: 16, padding: "2px 6px", borderRadius: 4, transition: "all 0.15s" }}
          onMouseEnter={e => { e.currentTarget.style.color = C.red; e.currentTarget.style.background = "#FEF2F2"; }}
          onMouseLeave={e => { e.currentTarget.style.color = C.t3; e.currentTarget.style.background = "none"; }}>×</button>
      )}
    </div>
  );
}

// ─── Expired warning modal ────────────────────────────────────
function ExpiredWarningModal({ onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 }}>
      <div style={{ background: C.white, borderRadius: 16, padding: "36px 32px", maxWidth: 440, width: "90%", boxShadow: "0 12px 48px rgba(0,0,0,0.18)", textAlign: "center" }}>
        <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 28 }}>⚠️</div>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: C.t1, margin: "0 0 12px" }}>Quote Expired</h3>
        <p style={{ fontSize: 14, color: C.t2, margin: "0 0 8px", lineHeight: 1.6 }}>
          Only valid (non-expired) quotes can be submitted for ordering.
        </p>
        <p style={{ fontSize: 13, color: C.t3, margin: "0 0 28px", lineHeight: 1.6 }}>
          Please submit a <strong>Quote Update Request</strong> to have Boal Systems review and refresh this quote before placing your order.
        </p>
        <button onClick={onClose}
          style={{ padding: "11px 32px", borderRadius: 10, border: "none", background: C.navy, color: C.white, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          Got It
        </button>
      </div>
    </div>
  );
}

// ─── Order submission modal ───────────────────────────────────
function MismatchMessage({ onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2100 }}>
      <div style={{ background: C.white, borderRadius: 16, padding: "36px 32px", maxWidth: 420, width: "90%", textAlign: "center", boxShadow: "0 12px 48px rgba(0,0,0,0.18)" }}>
        <div style={{ fontSize: 36, marginBottom: 16 }}>📋</div>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: C.t1, margin: "0 0 12px" }}>Please Contact Your Boal Rep</h3>
        <p style={{ fontSize: 14, color: C.t2, margin: "0 0 28px", lineHeight: 1.6 }}>
          Please contact your Boal Representative to update the quote in the portal so that your order can be processed as soon as possible.
        </p>
        <p style={{ fontSize: 13, color: C.t3, margin: "0 0 28px", lineHeight: 1.6, fontStyle: "italic" }}>
          Por favor contacte a su Representante de Boal para actualizar la cotización en el portal y así procesar su orden lo antes posible.
        </p>
        <button onClick={onClose}
          style={{ padding: "11px 32px", borderRadius: 10, border: "none", background: C.navy, color: C.white, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          Close / Cerrar
        </button>
      </div>
    </div>
  );
}

function OrderSubmissionModal({ quote, user, onClose, onSuccess }) {
  const [attested, setAttested]               = useState(false);
  const [showMismatch, setShowMismatch]       = useState(false);
  const [poFiles, setPoFiles]           = useState([]);
  const [transferFiles, setTransferFiles] = useState([]);
  const [submitting, setSubmitting]     = useState(false);
  const [error, setError]               = useState("");
  if (showMismatch) return <MismatchMessage onClose={() => { setShowMismatch(false); onClose(); }} />;

  const handleSubmit = async () => {
    if (poFiles.length === 0) { setError("Please upload your Purchase Order (PO) document before submitting."); return; }
    setError("");
    setSubmitting(true);
    try {
      const r = await apiCall("submitOrder", {
        quoteId:       quote.id,
        quoteNumber:   quote.quoteNumber,
        company:       quote.company || user.company,
        requestedBy:   user.name,
        email:         user.email,
        amount:        quote.amount,
        manufacturer:  quote.manufacturer,
        projectName:   quote.projectName,
        solutionType:  quote.solutionType,
        country:       quote.country,
        boalRep:       quote.boalRep || "",
        quoteDocs:     (quote.documents || []),
        poFiles:       poFiles.map(f => ({ name: f.name, mimeType: f.mimeType, base64: f.base64 })),
        transferFiles: transferFiles.map(f => ({ name: f.name, mimeType: f.mimeType, base64: f.base64 })),
      });
      if (r.success) {
        onSuccess();
      } else {
        setError(r.error || "Submission failed. Please try again.");
      }
    } catch (err) {
      console.error("Order submission failed:", err);
      setError("Submission failed. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 }}>
      <div style={{ background: C.white, borderRadius: 16, padding: "36px 32px", maxWidth: 520, width: "90%", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 12px 48px rgba(0,0,0,0.18)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <h3 style={{ fontSize: 20, fontWeight: 700, color: C.t1, margin: "0 0 4px" }}>Submit Order</h3>
            <p style={{ fontSize: 13, color: C.t2, margin: 0 }}>{quote.quoteNumber} · {quote.manufacturer}</p>
          </div>
          <button onClick={onClose}
            style={{ background: C.grayBg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 8px", cursor: "pointer", color: C.t3, display: "flex" }}
            onMouseEnter={e => { e.currentTarget.style.color = C.t1; }} onMouseLeave={e => { e.currentTarget.style.color = C.t3; }}>
            <Ic.X />
          </button>
        </div>

        {/* Quote summary */}
        <div style={{ padding: "14px 16px", borderRadius: 10, background: C.grayBg, border: `1px solid ${C.border}`, marginBottom: 24 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px", fontSize: 13 }}>
            <div><span style={{ color: C.t3 }}>Amount: </span><strong style={{ color: C.t1 }}>${Number(quote.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</strong></div>
            <div><span style={{ color: C.t3 }}>Company: </span><span style={{ color: C.t1 }}>{quote.company || "—"}</span></div>
            {quote.projectName && <div style={{ gridColumn: "1 / -1" }}><span style={{ color: C.t3 }}>Project: </span><span style={{ color: C.t1 }}>{quote.projectName}</span></div>}
          </div>
        </div>

        {/* Attestation gate */}
        {!attested ? (
          <div style={{ padding: "20px", borderRadius: 12, background: "#F0F9FF", border: "1px solid #BAE6FD", marginBottom: 24 }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: "#0C4A6E", margin: "0 0 8px" }}>
              Please review before uploading / Por favor revise antes de subir
            </p>
            <p style={{ fontSize: 13, color: "#0369A1", margin: "0 0 6px", lineHeight: 1.6 }}>
              Please confirm that your Purchase Order matches the quote document in the portal and the quote amount exactly.
            </p>
            <p style={{ fontSize: 13, color: "#0369A1", margin: "0 0 20px", lineHeight: 1.6, fontStyle: "italic" }}>
              Por favor confirme que su Orden de Compra coincide exactamente con el documento de cotización en el portal y el monto de la cotización. Si no coincide, el sistema rechazará la orden.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setAttested(true)}
                style={{ flex: 1, padding: "10px 16px", borderRadius: 10, border: "none", background: `linear-gradient(135deg,${C.navy},${C.navyLight})`, color: C.white, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                ✓ I Confirm / Confirmo
              </button>
              <button onClick={() => setShowMismatch(true)}
                style={{ flex: 1, padding: "10px 16px", borderRadius: 10, border: `1.5px solid ${C.border}`, background: C.white, color: C.t2, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                Documents Don't Match
              </button>
            </div>
          </div>
        ) : (
          <div style={{ padding: "8px 12px", borderRadius: 8, background: "#D1FAE5", border: "1px solid #6EE7B7", marginBottom: 16, fontSize: 12, color: "#065F46", fontWeight: 600 }}>
            ✓ Confirmed — your PO matches the quote document
          </div>
        )}

        {/* PO upload — required */}
        <div style={{ marginBottom: 24, display: attested ? "block" : "none" }}>
          <div style={{ marginBottom: 8 }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: C.t1, margin: "0 0 4px" }}>
              Purchase Order (PO) <span style={{ color: C.red }}>*</span>
            </p>
            <p style={{ fontSize: 12, color: C.t3, margin: 0 }}>Upload your signed PO document (PDF required).</p>
          </div>
          <FileUpload files={poFiles} onChange={setPoFiles} accept=".pdf,application/pdf" />
          {poFiles.length > 0 && (
            <div style={{ marginTop: 6, fontSize: 12, color: C.ok }}>✓ {poFiles.length} file{poFiles.length > 1 ? "s" : ""} ready</div>
          )}
        </div>

        {/* Bank transfer — optional */}
        <div style={{ marginBottom: 24, display: attested ? "block" : "none" }}>
          <div style={{ marginBottom: 8 }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: C.t1, margin: "0 0 4px" }}>Bank Transfer Confirmation</p>
            <p style={{ fontSize: 12, color: C.t3, margin: 0 }}>Optional — upload a screenshot or PDF of your bank transfer confirmation.</p>
          </div>
          <FileUpload files={transferFiles} onChange={setTransferFiles} />
          {transferFiles.length > 0 && (
            <div style={{ marginTop: 6, fontSize: 12, color: C.ok }}>✓ {transferFiles.length} file{transferFiles.length > 1 ? "s" : ""} ready</div>
          )}
        </div>

        {attested && error && (
          <div style={{ padding: "10px 14px", borderRadius: 8, background: "#FEF2F2", border: "1px solid #FECACA", marginBottom: 16 }}>
            <p style={{ fontSize: 13, color: C.red, margin: 0 }}>{error}</p>
          </div>
        )}

        <div style={{ display: attested ? "flex" : "none", gap: 12, justifyContent: "flex-end" }}>
          <button onClick={onClose}
            style={{ padding: "11px 24px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.white, color: C.t1, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={submitting}
            style={{ padding: "11px 28px", borderRadius: 10, border: "none", background: submitting ? `${C.navy}80` : `linear-gradient(135deg,${C.navy} 0%,${C.navyLight} 100%)`, color: C.white, fontSize: 14, fontWeight: 600, cursor: submitting ? "not-allowed" : "pointer", fontFamily: "inherit", boxShadow: submitting ? "none" : `0 4px 14px ${C.navy}30` }}>
            {submitting ? "Submitting..." : "Submit Order"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Order success modal ──────────────────────────────────────
function OrderSuccessModal({ quoteNumber, onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 }}>
      <div style={{ background: C.white, borderRadius: 16, padding: "40px 36px", maxWidth: 420, width: "90%", textAlign: "center", boxShadow: "0 12px 48px rgba(0,0,0,0.18)" }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#D1FAE5", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 32 }}>✓</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: C.t1, margin: "0 0 8px" }}>Order Submitted!</h2>
        <p style={{ fontSize: 14, color: C.t2, margin: "0 0 6px", lineHeight: 1.6 }}>
          Your order for <strong>{quoteNumber}</strong> has been submitted and is now pending review by Boal Systems.
        </p>
        <p style={{ fontSize: 13, color: C.t3, margin: "0 0 28px", lineHeight: 1.6 }}>
          You can track the status of your order in the Order Status & Tracking module.
        </p>
        <button onClick={onClose}
          style={{ padding: "12px 36px", borderRadius: 10, border: "none", background: `linear-gradient(135deg,${C.navy} 0%,${C.navyLight} 100%)`, color: C.white, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          Done
        </button>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────
export default function QuoteDetail({ quote, isAdmin, user, onClose, onStatusChange, onUpdate, autoOrder = false }) {
  const [current, setCurrent]               = useState(quote);
  const [newQuoteFiles, setNewQuoteFiles]   = useState([]);
  const [uploadingQuote, setUploadingQuote] = useState(false);
  const [newSupportFiles, setNewSupportFiles] = useState([]);
  const [uploadingSupport, setUploadingSupport] = useState(false);
  const [savingExpiry, setSavingExpiry]     = useState(false);
  const [editExpiry, setEditExpiry]         = useState(false);
  const [expiryVal, setExpiryVal]           = useState(current.expiryDate || "");
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [linkedRequest, setLinkedRequest]   = useState(null);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [editingProject, setEditingProject] = useState(false);
  const [projectVal, setProjectVal]         = useState(current.projectName || "");
  const [savingProject, setSavingProject]   = useState(false);

  // Order flow state — autoOrder opens the modal immediately if quote is valid
  const [showExpiredWarning, setShowExpiredWarning] = useState(() => autoOrder && (quote.expiryDate && new Date() > new Date(quote.expiryDate)));
  const [showOrderModal, setShowOrderModal]         = useState(() => autoOrder && !(quote.expiryDate && new Date() > new Date(quote.expiryDate)));
  const [showOrderSuccess, setShowOrderSuccess]     = useState(false);

  const isExpired = current.expiryDate && new Date() > new Date(current.expiryDate);

  const fmt = (val) => {
    if (!val && val !== 0) return "—";
    return "$" + Number(val).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === (current.status || "Open")) return;

    // Intercept "Ordered" — show order submission flow instead of direct status change
    if (newStatus === "Ordered") {
      if (isExpired) {
        setShowExpiredWarning(true);
      } else {
        setShowOrderModal(true);
      }
      return;
    }

    try {
      await apiCall("updateQuoteStatus", { quoteId: current.id, status: newStatus });
      const updated = { ...current, status: newStatus };
      setCurrent(updated);
      onStatusChange(current.id, newStatus);
      onUpdate(updated);
    } catch (err) { console.error("Failed to update status:", err); }
  };

  const handleOrderSuccess = () => {
    setShowOrderModal(false);
    const updated = { ...current, status: "Ordered" };
    setCurrent(updated);
    onStatusChange(current.id, "Ordered");
    onUpdate(updated);
    setShowOrderSuccess(true);
  };

  const handleSaveExpiry = async () => {
    setSavingExpiry(true);
    try {
      await apiCall("updateQuoteExpiry", { quoteId: current.id, expiryDate: expiryVal });
      const updated = { ...current, expiryDate: expiryVal };
      setCurrent(updated);
      onUpdate(updated);
      setEditExpiry(false);
    } catch (err) { console.error("Failed to update expiry:", err); }
    setSavingExpiry(false);
  };

  const handleSaveProject = async () => {
    setSavingProject(true);
    try {
      await apiCall("updateQuoteField", { quoteId: current.id, field: "projectName", value: projectVal });
      const updated = { ...current, projectName: projectVal };
      setCurrent(updated);
      onUpdate(updated);
      setEditingProject(false);
    } catch (err) { console.error("Failed to update project name:", err); }
    setSavingProject(false);
  };

  const handleUpload = async (files, setFiles, setUploading) => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      const r = await apiCall("uploadQuoteFile", {
        quoteId: current.id,
        files: files.map(f => ({ name: f.name, mimeType: f.mimeType, base64: f.base64 })),
      });
      if (r.success) {
        const updated = { ...current, documents: r.documents };
        setCurrent(updated);
        onUpdate(updated);
        setFiles([]);
      }
    } catch (err) { console.error("Upload failed:", err); }
    setUploading(false);
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      const r = await apiCall("deleteQuoteFile", { quoteId: current.id, fileId });
      if (r.success) {
        const updated = { ...current, documents: r.documents };
        setCurrent(updated);
        onUpdate(updated);
      }
    } catch (err) { console.error("Delete failed:", err); }
  };

  const handleOpenLinkedRequest = async () => {
    if (!current.requestId) return;
    setLoadingRequest(true);
    try {
      const r = await apiCall("getRequests", { email: user.email, company: user.company, role: user.role || "CLIENT" });
      if (r.success) {
        const req = r.requests.find(req => req.id === current.requestId);
        if (req) setLinkedRequest(req);
      }
    } catch (err) { console.error("Failed to load request:", err); }
    setLoadingRequest(false);
  };

  const docs = Array.isArray(current.documents) ? current.documents :
    (typeof current.documents === "string" && current.documents
      ? (() => { try { return JSON.parse(current.documents); } catch { return []; } })()
      : []);

  const sc = STATUS_COLORS[current.status] || STATUS_COLORS["Open"];

  return (
    <>
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", justifyContent: "flex-end", zIndex: 1000 }}
        onClick={onClose}>
        <div style={{ width: "100%", maxWidth: 620, height: "100%", background: C.white, boxShadow: "-8px 0 40px rgba(0,0,0,0.12)", overflowY: "auto", animation: "slideIn 0.25s ease" }}
          onClick={e => e.stopPropagation()}>

          {/* Header */}
          <div style={{ padding: "20px 28px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: C.white, zIndex: 1 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                <span style={{ fontSize: 18, fontWeight: 700, color: C.info }}>{current.quoteNumber}</span>
                <span style={{ padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600, background: sc.bg, color: sc.c }}>{current.status || "Open"}</span>
                <span style={{ padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600, background: isExpired ? "#FEE2E2" : "#D1FAE5", color: isExpired ? "#991B1B" : "#065F46" }}>
                  {isExpired ? "Expired" : "Valid"}
                </span>
              </div>
              <div style={{ fontSize: 13, color: C.t2 }}>{current.manufacturer || current.solutionType || "—"}</div>
            </div>
            <button onClick={onClose}
              style={{ background: C.grayBg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 10px", cursor: "pointer", color: C.t3, display: "flex" }}
              onMouseEnter={e => { e.currentTarget.style.color = C.t1; e.currentTarget.style.borderColor = C.t3; }}
              onMouseLeave={e => { e.currentTarget.style.color = C.t3; e.currentTarget.style.borderColor = C.border; }}>
              <Ic.X />
            </button>
          </div>

          {/* Status toggle bar */}
          <div style={{ padding: "12px 28px", background: `${C.navy}06`, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>Status</span>
            <div style={{ display: "flex", gap: 6 }}>
              {STATUS_OPTIONS.map(opt => {
                const active = opt === (current.status || "Open");
                const sc2 = STATUS_COLORS[opt];
                return (
                  <button key={opt} onClick={() => handleStatusChange(opt)}
                    style={{
                      padding: "4px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600,
                      cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
                      border: `1.5px solid ${active ? sc2.c + "60" : C.border}`,
                      background: active ? sc2.bg : C.white,
                      color: active ? sc2.c : C.t3,
                    }}
                    onMouseEnter={e => { if (!active) { e.currentTarget.style.background = sc2.bg; e.currentTarget.style.color = sc2.c; e.currentTarget.style.borderColor = sc2.c + "60"; }}}
                    onMouseLeave={e => { if (!active) { e.currentTarget.style.background = C.white; e.currentTarget.style.color = C.t3; e.currentTarget.style.borderColor = C.border; }}}>
                    {opt}
                  </button>
                );
              })}
            </div>
            {isExpired && (current.status || "Open") === "Open" && (
              <span style={{ fontSize: 11, color: "#991B1B", marginLeft: 4 }}>⚠ Expired quotes cannot be ordered</span>
            )}
          </div>

          {/* Meta strip */}
          <div style={{ padding: "14px 28px", background: C.grayBg, borderBottom: `1px solid ${C.border}`, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div><div style={{ fontSize: 10, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em" }}>Quoted Date</div><div style={{ fontSize: 13, color: C.t1, fontWeight: 500 }}>{fmtDate(current.quotedDate)}</div></div>
            <div><div style={{ fontSize: 10, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em" }}>Requested By</div><div style={{ fontSize: 13, color: C.t1 }}>{current.requestedBy || "—"}</div></div>
            <div><div style={{ fontSize: 10, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em" }}>Company</div><div style={{ fontSize: 13, color: C.t1 }}>{current.company || "—"}</div></div>
          </div>

          {/* Body */}
          <div style={{ padding: "8px 28px 28px" }}>
            <Section title="Quote Details" />

            <Field label="Quote Amount">
              <div style={{ fontSize: 15, fontWeight: 700, color: C.t1 }}>{fmt(current.amount)}</div>
            </Field>
            <Field label="Manufacturer" value={current.manufacturer} />
            <Field label="Solution Type" value={current.solutionType} />
            <Field label="Country" value={current.country} />

            <Field label="Project Name">
              {editingProject ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
                  <input value={projectVal} onChange={e => setProjectVal(e.target.value)} autoFocus
                    onKeyDown={e => { if (e.key === "Enter") handleSaveProject(); if (e.key === "Escape") setEditingProject(false); }}
                    style={{ flex: 1, padding: "6px 10px", borderRadius: 6, border: `1px solid ${C.navy}40`, background: C.grayBg, fontSize: 13, color: C.t1, outline: "none", fontFamily: "inherit" }} />
                  <button onClick={handleSaveProject} disabled={savingProject}
                    style={{ background: C.navy, border: "none", borderRadius: 6, padding: "6px 12px", cursor: "pointer", color: C.white, fontSize: 13, fontFamily: "inherit" }}>
                    {savingProject ? "Saving..." : "Save"}
                  </button>
                  <button onClick={() => { setEditingProject(false); setProjectVal(current.projectName || ""); }}
                    style={{ background: C.grayBg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px 10px", cursor: "pointer", color: C.t3, fontSize: 13, fontFamily: "inherit" }}>
                    Cancel
                  </button>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 14, color: current.projectName ? C.t1 : C.t3 }}>{current.projectName || "—"}</span>
                  <button onClick={() => setEditingProject(true)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: C.t3, padding: 4, borderRadius: 6, display: "flex" }}
                    onMouseEnter={e => { e.currentTarget.style.color = C.navy; e.currentTarget.style.background = C.blueLight; }}
                    onMouseLeave={e => { e.currentTarget.style.color = C.t3; e.currentTarget.style.background = "none"; }}>
                    <Ic.Edit />
                  </button>
                </div>
              )}
            </Field>

            <Field label="Quote Expiry Date">
              {isAdmin && editExpiry ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
                  <input type="date" value={expiryVal} onChange={e => setExpiryVal(e.target.value)}
                    style={{ padding: "6px 10px", borderRadius: 6, border: `1px solid ${C.navy}40`, background: C.grayBg, fontSize: 13, color: C.t1, outline: "none", fontFamily: "inherit" }} />
                  <button onClick={handleSaveExpiry} disabled={savingExpiry}
                    style={{ background: C.navy, border: "none", borderRadius: 6, padding: "6px 12px", cursor: "pointer", color: C.white, fontSize: 13, fontFamily: "inherit" }}>
                    {savingExpiry ? "Saving..." : "Save"}
                  </button>
                  <button onClick={() => { setEditExpiry(false); setExpiryVal(current.expiryDate || ""); }}
                    style={{ background: C.grayBg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px 10px", cursor: "pointer", color: C.t3, fontSize: 13, fontFamily: "inherit" }}>
                    Cancel
                  </button>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 14, color: C.t1 }}>{fmtDate(current.expiryDate)}</span>
                  {isAdmin && (
                    <button onClick={() => setEditExpiry(true)}
                      style={{ background: "none", border: "none", cursor: "pointer", color: C.t3, padding: 4, borderRadius: 6, display: "flex" }}
                      onMouseEnter={e => { e.currentTarget.style.color = C.navy; e.currentTarget.style.background = C.blueLight; }}
                      onMouseLeave={e => { e.currentTarget.style.color = C.t3; e.currentTarget.style.background = "none"; }}>
                      <Ic.Edit />
                    </button>
                  )}
                </div>
              )}
            </Field>

            <Field label="Linked Request ID">
              {current.requestId ? (
                <button onClick={handleOpenLinkedRequest} disabled={loadingRequest}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit", fontSize: 13, fontWeight: 600, color: C.info, textDecoration: "underline", opacity: loadingRequest ? 0.6 : 1 }}>
                  {loadingRequest ? "Loading..." : current.requestId} ↗
                </button>
              ) : (
                <span style={{ fontSize: 13, color: C.t3 }}>—</span>
              )}
            </Field>

            {/* Quote File */}
            <Section title="Quote File" />
            {docs.length > 0 ? (
              <div style={{ marginTop: 12 }}>
                {docs.map((doc, i) => (
                  <DocRow key={doc.id || i} doc={doc} isAdmin={isAdmin} onDelete={handleDeleteFile} />
                ))}
              </div>
            ) : (
              <p style={{ fontSize: 13, color: C.t3, padding: "12px 0" }}>No quote file attached.</p>
            )}
            {isAdmin && (
              <div style={{ marginTop: 12 }}>
                <FileUpload files={newQuoteFiles} onChange={setNewQuoteFiles} />
                {newQuoteFiles.length > 0 && (
                  <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
                    <button onClick={() => handleUpload(newQuoteFiles, setNewQuoteFiles, setUploadingQuote)} disabled={uploadingQuote}
                      style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: C.navy, color: C.white, fontSize: 13, fontWeight: 600, cursor: uploadingQuote ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: uploadingQuote ? 0.7 : 1 }}>
                      {uploadingQuote ? "Uploading..." : `Upload ${newQuoteFiles.length} file${newQuoteFiles.length > 1 ? "s" : ""}`}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Supporting Documents */}
            <Section title="Supporting Documents" />
            <p style={{ fontSize: 12, color: C.t3, margin: "8px 0 12px" }}>
              {isAdmin ? "Upload calculation reports and supporting files for the client to view." : "Supporting documents provided by Boal Systems."}
            </p>
            {!isAdmin && docs.length === 0 && (
              <p style={{ fontSize: 13, color: C.t3, padding: "4px 0 12px" }}>No supporting documents attached.</p>
            )}
            {isAdmin && (
              <div style={{ marginTop: 4 }}>
                <FileUpload files={newSupportFiles} onChange={setNewSupportFiles} />
                {newSupportFiles.length > 0 && (
                  <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
                    <button onClick={() => handleUpload(newSupportFiles, setNewSupportFiles, setUploadingSupport)} disabled={uploadingSupport}
                      style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: C.navy, color: C.white, fontSize: 13, fontWeight: 600, cursor: uploadingSupport ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: uploadingSupport ? 0.7 : 1 }}>
                      {uploadingSupport ? "Uploading..." : `Upload ${newSupportFiles.length} file${newSupportFiles.length > 1 ? "s" : ""}`}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Request Quote Update — clients only */}
            {!isAdmin && (
              <div style={{ marginTop: 28, paddingTop: 20, borderTop: `1px solid ${C.border}` }}>
                <button onClick={() => setShowUpdateModal(true)}
                  style={{ padding: "11px 24px", borderRadius: 8, border: `1.5px solid ${C.navy}`, background: C.white, color: C.navy, fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = C.navy; e.currentTarget.style.color = C.white; }}
                  onMouseLeave={e => { e.currentTarget.style.background = C.white; e.currentTarget.style.color = C.navy; }}>
                  Request Quote Update
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showExpiredWarning && <ExpiredWarningModal onClose={() => setShowExpiredWarning(false)} />}
      {showOrderModal && (
        <OrderSubmissionModal
          quote={current}
          user={user}
          onClose={() => setShowOrderModal(false)}
          onSuccess={handleOrderSuccess}
        />
      )}
      {showOrderSuccess && (
        <OrderSuccessModal
          quoteNumber={current.quoteNumber}
          onClose={() => { setShowOrderSuccess(false); onClose(); }}
        />
      )}
      {linkedRequest && (
        <SubmissionDetail submission={linkedRequest} isAdmin={isAdmin} onClose={() => setLinkedRequest(null)} onUpdate={() => {}} />
      )}
      {showUpdateModal && (
        <UpdateQuoteModal quote={current} user={user} onClose={() => setShowUpdateModal(false)} />
      )}

      <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
    </>
  );
}
